import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, AlertTriangle, ClipboardX, RefreshCw, TrendingUp, Calendar } from 'lucide-react';
import ApiService from './ApiService';

const PatientStatsWidget = ({ config, patients, onRefresh }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = config?.terminology || {
    client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' }
  };

  // Load patient statistics
  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all stats in parallel
      const [duplicates, noProtocols, totalStats] = await Promise.all([
        ApiService.getLatestDuplicateScan(),
        ApiService.getPatientsWithoutProtocols(),
        ApiService.getPatientStats()
      ]);

      setStats({
        totalPatients: totalStats.total || patients.length,
        activePatients: totalStats.active || patients.length,
        newThisMonth: totalStats.newThisMonth || 0,
        duplicateGroups: duplicates.summary?.duplicateGroups || 0,
        totalDuplicates: duplicates.summary?.totalDuplicates || 0,
        lastDuplicateScan: duplicates.summary?.scanDate,
        noProtocolsCount: noProtocols.count || 0,
        noProtocolsPatients: noProtocols.patients || []
      });

    } catch (err) {
      console.error('Failed to load patient stats:', err);
      setError('Failed to load statistics');
      // Set fallback stats
      setStats({
        totalPatients: patients.length,
        activePatients: patients.length,
        newThisMonth: 0,
        duplicateGroups: 0,
        totalDuplicates: 0,
        noProtocolsCount: 0,
        noProtocolsPatients: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [patients.length]);

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-spruce-600" size={24} />
          <span className="ml-2 text-gray-600">Loading statistics...</span>
        </div>
      </div>
    );
  }

  const duplicateColor = stats?.totalDuplicates === 0 ? 'green' : stats?.totalDuplicates <= 4 ? 'yellow' : 'red';
  const noProtocolsColor = stats?.noProtocolsCount === 0 ? 'green' : stats?.noProtocolsCount <= 5 ? 'yellow' : 'red';

  return (
    <div className="mb-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        
        {/* Total Patients */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-spruce-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase">Total {t.client.plural}</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats?.totalPatients || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.activePatients || 0} active
              </p>
            </div>
            <div className="p-3 bg-spruce-100 rounded-full">
              <Users className="text-spruce-600" size={28} />
            </div>
          </div>
        </div>

        {/* New This Month */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase">New This Month</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats?.newThisMonth || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                <Calendar size={12} className="inline mr-1" />
                {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="text-green-600" size={28} />
            </div>
          </div>
        </div>

        {/* Duplicate Patients - CLICKABLE TO NAVIGATE */}
        <div 
          className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
            duplicateColor === 'green' ? 'border-green-500' : 
            duplicateColor === 'yellow' ? 'border-yellow-500' : 
            'border-red-500'
          } cursor-pointer hover:shadow-lg transition`}
          onClick={() => navigate('/duplicates')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase">Duplicate {t.client.plural}</p>
              <p className={`text-3xl font-bold mt-1 ${
                duplicateColor === 'green' ? 'text-green-600' : 
                duplicateColor === 'yellow' ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {stats?.totalDuplicates || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.duplicateGroups || 0} group{stats?.duplicateGroups !== 1 ? 's' : ''}
              </p>
              {stats?.lastDuplicateScan && (
                <p className="text-xs text-gray-400 mt-1">
                  Last scan: {new Date(stats.lastDuplicateScan).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className={`p-3 rounded-full ${
              duplicateColor === 'green' ? 'bg-green-100' : 
              duplicateColor === 'yellow' ? 'bg-yellow-100' : 
              'bg-red-100'
            }`}>
              <AlertTriangle className={
                duplicateColor === 'green' ? 'text-green-600' : 
                duplicateColor === 'yellow' ? 'text-yellow-600' : 
                'text-red-600'
              } size={28} />
            </div>
          </div>
          <div className="mt-2 text-xs text-spruce-600 font-medium">
            Click to view and resolve →
          </div>
        </div>

        {/* No Protocols */}
        <div 
          className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
            noProtocolsColor === 'green' ? 'border-green-500' : 
            noProtocolsColor === 'yellow' ? 'border-yellow-500' : 
            'border-red-500'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase">No Protocols</p>
              <p className={`text-3xl font-bold mt-1 ${
                noProtocolsColor === 'green' ? 'text-green-600' : 
                noProtocolsColor === 'yellow' ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {stats?.noProtocolsCount || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t.client.pluralLower} unassigned
              </p>
            </div>
            <div className={`p-3 rounded-full ${
              noProtocolsColor === 'green' ? 'bg-green-100' : 
              noProtocolsColor === 'yellow' ? 'bg-yellow-100' : 
              'bg-red-100'
            }`}>
              <ClipboardX className={
                noProtocolsColor === 'green' ? 'text-green-600' : 
                noProtocolsColor === 'yellow' ? 'text-yellow-600' : 
                'text-red-600'
              } size={28} />
            </div>
          </div>
        </div>

      </div>

      {/* Quick Summary Bar */}
      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="text-gray-600">
            <strong>{stats?.totalPatients || 0}</strong> total {t.client.pluralLower}
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">
            <strong>{stats?.newThisMonth || 0}</strong> new this month
          </span>
          <span className="text-gray-400">•</span>
          <span className={duplicateColor === 'green' ? 'text-green-600' : duplicateColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'}>
            <strong>{stats?.totalDuplicates || 0}</strong> duplicates
          </span>
          <span className="text-gray-400">•</span>
          <span className={noProtocolsColor === 'green' ? 'text-green-600' : noProtocolsColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'}>
            <strong>{stats?.noProtocolsCount || 0}</strong> without protocols
          </span>
        </div>
        <button
          onClick={loadStats}
          className="text-spruce-600 hover:text-spruce-800 flex items-center gap-1"
        >
          <RefreshCw size={14} />
          Refresh Stats
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

export default PatientStatsWidget;